'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  organization: string;
  quote: string;
  image: string;
  alt: string;
}

const TestimonialSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Ms. John",
    role: "NAF Academy of Engineering Director",
    organization: "NAF Academy",
    quote: "I want to express my full support for Farms for Thought's endeavors... their curriculum made students who never participate, engaged in difficult coursework.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d07eb19-1763299289544.png",
    alt: "Professional woman with brown hair in navy blazer smiling confidently in modern office setting"
  },
  {
    id: 2,
    name: "Ms. Graham",
    role: "Media Specialist",
    organization: "Partner School",
    quote: "The content was fantastic and delivered in such a way that both teachers and students present were able to see food insecurity in a completely different light. I often went home after Farms for Thought's visits and told my family what I learned. It was really impactful.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0486bac-1763295655768.png",
    alt: "Professional educator smiling warmly in educational setting"
  },
  {
    id: 3,
    name: "Student Certification",
    role: "Program Achievement",
    organization: "Farms for Thought Foundation",
    quote: "Students who complete the course with an 80% average on activities receive certification, and are eligible to request recommendation letters that speak to their skills. Our students use technology and platforms commonly introduced in 'Introduction to Computer Science' and 'Rapid Prototyping' courses at the collegiate level.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f585ace3-1769476701180.png",
    alt: "Student achievement and certification program representation"
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-background">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Voices of Impact
            </h2>
            <p className="text-lg md:text-xl text-text-secondary">
              Hear from the educators, parents, and partners making a difference
            </p>
          </div>

          <div className="bg-card rounded-2xl shadow-brand-md p-8 md:p-12">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-32 h-32 flex-shrink-0">
                <AppImage
                  src={testimonials[0].image}
                  alt={testimonials[0].alt}
                  className="w-full h-full rounded-full object-cover" />

              </div>
              <div className="flex-1 text-center md:text-left">
                <Icon name="ChatBubbleLeftIcon" size={40} className="text-brand-primary mb-4 mx-auto md:mx-0" />
                <p className="text-lg md:text-xl text-text-primary leading-relaxed mb-6 italic">
                  "{testimonials[0].quote}"
                </p>
                <div>
                  <div className="font-bold text-lg text-text-primary">{testimonials[0].name}</div>
                  <div className="text-base text-text-secondary">{testimonials[0].role}</div>
                  <div className="text-sm text-text-secondary">{testimonials[0].organization}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-20 bg-background">
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Voices of Impact
          </h2>
          <p className="text-lg md:text-xl text-text-secondary">
            Hear from the educators, parents, and partners making a difference
          </p>
        </div>

        <div className="relative">
          <div className="bg-card rounded-2xl shadow-brand-md p-8 md:p-12 transition-all duration-500">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-1 text-center md:text-left">
                <Icon name="ChatBubbleLeftIcon" size={40} className="text-brand-primary mb-4 mx-auto md:mx-0" />
                <p className="text-lg md:text-xl text-text-primary leading-relaxed mb-6 italic">
                  "{testimonials[currentIndex].quote}"
                </p>
                <div>
                  <div className="font-bold text-lg text-text-primary">{testimonials[currentIndex].name}</div>
                  <div className="text-base text-text-secondary">{testimonials[currentIndex].role}</div>
                  <div className="text-sm text-text-secondary">{testimonials[currentIndex].organization}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex ?
              'bg-brand-primary w-8' : 'bg-border hover:bg-brand-primary/50'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialSection;